import { FC, useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import useProductHistory, { HistoryTimeRange } from '@/hooks/useProductHistory';
import { useTranslation } from 'react-i18next';
import Callout from '../callout/callout';
import DataBoxHolder from '@/components/dataBox/dataBoxHolder';
import SmallDataBox from '@/components/dataBox/smallDataBox';
import SelectInput from '@/components/inputs/selectInput';

interface ProductAmountHistoryChartProps {
    storageId: string;
    productId: string;
    productUnit?: string;
}

const ProductAmountHistoryChart: FC<ProductAmountHistoryChartProps> = ({ 
    storageId, 
    productId,
    productUnit = 'units'
}) => {
    const { t } = useTranslation();
    
    // Initialize from localStorage or use defaults
    const getInitialTimeRange = (): HistoryTimeRange => {
        const saved = localStorage.getItem('productHistoryTimeRange');
        if (saved) {
            const parsed = parseInt(saved);
            if (Object.values(HistoryTimeRange).includes(parsed)) {
                return parsed as HistoryTimeRange;
            }
        }
        return HistoryTimeRange.MONTH;
    };

    const getInitialChartType = (): 'bar' | 'line' | 'mix' => {
        const saved = localStorage.getItem('productHistoryChartType');
        if (saved === 'bar' || saved === 'line' || saved === 'mix') {
            return saved;
        }
        return 'bar';
    };

    const [timeRange, setTimeRange] = useState<HistoryTimeRange>(getInitialTimeRange());
    const [chartType, setChartType] = useState<'bar' | 'line' | 'mix'>(getInitialChartType());

    // Save to localStorage when values change
    useEffect(() => {
        localStorage.setItem('productHistoryTimeRange', timeRange.toString());
    }, [timeRange]);

    useEffect(() => {
        localStorage.setItem('productHistoryChartType', chartType);
    }, [chartType]);

    const { history, isLoading, error } = useProductHistory({
        storageId,
        productId,
        timeRange,
    });

    // Format date for X-axis based on time range
    const formatXAxis = (timestampString: string) => {
        const date = new Date(timestampString);
        
        switch (timeRange) {
            case HistoryTimeRange.WEEK:
                // Show day and time for 7 days
                return date.toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                });
            case HistoryTimeRange.MONTH:
                // Show day for 30 days
                return date.toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric' 
                });
            case HistoryTimeRange.QUARTER:
                // Show date for 90 days
                return date.toLocaleDateString(undefined, { 
                    month: 'short', 
                    day: 'numeric' 
                });
            case HistoryTimeRange.YEAR:
                // Show month for year view
                return date.toLocaleDateString(undefined, { 
                    month: 'short',
                    year: '2-digit'
                });
            default:
                return date.toLocaleDateString();
        }
    };

    // Prepare chart data
    const chartData = history.map((entry) => {
        const timestamp = entry.keyHistory?.createdAt?.toDate()?.toISOString() || '';
        return {
            timestamp,
            displayDate: formatXAxis(timestamp),
            amount: entry.amount,
            delta: entry.delta,
            actionType: entry.actionType,
            // Separate deltas by type for color coding
            increment: entry.actionType === 'increment' ? entry.delta : 0,
            decrement: entry.actionType === 'decrement' ? Math.abs(entry.delta) : 0,
            adjust: entry.actionType === 'adjust' ? entry.delta : 0,
        };
    });

    // Custom tooltip
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const date = new Date(data.timestamp);
            
            return (
                <div className="bg-white p-4 border border-gray-200 rounded-md shadow-md">
                    <p className="font-bold mb-2">
                        {date.toLocaleDateString(undefined, { 
                            year: 'numeric',
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </p>
                    <p className="text-sm">
                        <span className="font-semibold">{t('amount')}: </span>
                        {data.amount} {productUnit}
                    </p>
                    <p className="text-sm">
                        <span className="font-semibold">{t('change')}: </span>
                        <span className={data.delta >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {data.delta >= 0 ? '+' : ''}{data.delta} {productUnit}
                        </span>
                    </p>
                    <p className="text-sm capitalize">
                        <span className="font-semibold">{t('actionType')}: </span>
                        {data.actionType}
                    </p>
                </div>
            );
        }
        return null;
    };

    if (error) {
        return <Callout type="error">{t('failedToLoadHistory')}</Callout>
    }

    if (isLoading) {
        return (
            <div className="p-4">
                <div className="h-96 bg-gray-100 animate-pulse rounded-md"></div>
            </div>
        );
    }

    if (!history || history.length === 0) {
        return <Callout type="info">{t('noHistoryData')}</Callout>
    }

    return (
        <div className="w-full">
            {/* Time Range Selector and Chart Type Selector */}
            <div className="flex gap-4 mb-4 flex-wrap items-end">
                <div className="flex-1 min-w-[200px]">
                    <SelectInput
                        label={t('timeRange')}
                        value={timeRange.toString()}
                        onChange={(e) => setTimeRange(parseInt(e.target.value) as HistoryTimeRange)}
                        ignore={true}
                        options={[
                            { value: HistoryTimeRange.WEEK.toString(), label: t('last7Days') },
                            { value: HistoryTimeRange.MONTH.toString(), label: t('last30Days') },
                            { value: HistoryTimeRange.QUARTER.toString(), label: t('last90Days') },
                            { value: HistoryTimeRange.YEAR.toString(), label: t('lastYear') },
                        ]}
                    />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <SelectInput
                        label={t('chartType')}
                        value={chartType}
                        onChange={(e) => setChartType(e.target.value as 'bar' | 'line' | 'mix')}
                        ignore={true}
                        options={[
                            { value: 'bar', label: t('barChart') },
                            { value: 'line', label: t('lineChart') },
                            { value: 'mix', label: t('mixChart') },
                        ]}
                    />
                </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={400}>
                {chartType === 'bar' ? (
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="displayDate" 
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval="preserveStartEnd"
                        />
                        <YAxis 
                            label={{ value: `${t('stockAmount')} (${productUnit})`, angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            verticalAlign="top"
                            height={36}
                        />
                        <Bar 
                            dataKey="amount" 
                            fill="#3b82f6" 
                            name={t('stockLevel')}
                            radius={[4, 4, 0, 0]}
                        />
                    </BarChart>
                ) : chartType === 'line' ? (
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="displayDate" 
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval="preserveStartEnd"
                        />
                        <YAxis 
                            label={{ value: `${t('stockAmount')} (${productUnit})`, angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            verticalAlign="top"
                            height={36}
                        />
                        <Line 
                            type="monotone"
                            dataKey="amount" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', r: 4 }}
                            activeDot={{ r: 6 }}
                            name={t('stockLevel')}
                        />
                    </LineChart>
                ) : (
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                            dataKey="displayDate" 
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval="preserveStartEnd"
                        />
                        <YAxis 
                            label={{ value: `${t('stockAmount')} (${productUnit})`, angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            verticalAlign="top"
                            height={36}
                        />
                        <Bar 
                            dataKey="amount" 
                            fill="#3b82f6" 
                            fillOpacity={0.3}
                            name={t('stockLevel')}
                            radius={[4, 4, 0, 0]}
                        />
                        <Line 
                            type="monotone"
                            dataKey="amount" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', r: 4 }}
                            activeDot={{ r: 6 }}
                            name={t('stockLevel')}
                        />
                    </LineChart>
                )}
            </ResponsiveContainer>

            {/* Summary */}
            {/*<DataBoxHolder>
                <SmallDataBox
                    label={t('totalIncreases')}
                    value={`+${chartData.reduce((sum, item) => sum + item.increment, 0)} ${productUnit}`}
                    colorScheme="green"
                    isLoading={false}
                />
                <SmallDataBox
                    label={t('totalDecreases')}
                    value={`-${chartData.reduce((sum, item) => sum + item.decrement, 0)} ${productUnit}`}
                    colorScheme="red"
                    isLoading={false}
                />
                <SmallDataBox
                    label={t('currentStock')}
                    value={`${chartData.length > 0 ? chartData[chartData.length - 1].amount : 0} ${productUnit}`}
                    colorScheme="blue"
                    isLoading={false}
                />
            </DataBoxHolder>*/}
        </div>
    );
};

export default ProductAmountHistoryChart;
